import {
  Category,
  Game,
  GameBadgeType,
  GameRecord,
  GameTesera,
  SortType,
} from "../interfaces";

export class DataService {
  private collectionsPath = "/data/collections/";
  private collections = [
    {
      file: "john.json",
      unavailableFile: "unavailable-john.json",
      owner: "John",
    },
    {
      file: "sergei.json",
      unavailableFile: "unavailable-sergei.json",
      owner: "Sergei",
    },
    {
      file: "club.json",
      unavailableFile: "unavailable-club.json",
      owner: "Community",
    },
    {
      file: "andrew.json",
      unavailableFile: "unavailable-andrew.json",
      owner: "Andrew",
    },
    {
      file: "guests.json",
      unavailableFile: "unavailable-guests.json",
      owner: "Guests",
    },
  ];

  categories: readonly Category[] = [
    {
      id: "hotness",
      title: "горячие",
      image: "/images/categories/hotness.svg",
      file: "hotness-bgg.json",
    },
    {
      id: "top",
      title: "топ 100",
      image: "/images/categories/top.svg",
      file: "top-bgg.json",
      sortType: "TopBgg",
    },
    {
      id: "party",
      title: "вечериночные",
      image: "/images/categories/party.svg",
      file: "party.json",
    },
    {
      id: "family",
      title: "семейные",
      image: "/images/categories/family.svg",
      file: "family.json",
    },
    {
      id: "children",
      title: "детские",
      image: "/images/categories/children.svg",
      file: "children.json",
    },
    {
      id: "hardcore",
      title: "хардкор",
      image: "/images/categories/hardcore.svg",
      file: "hardcore.json",
    },
  ] as const;

  async getCategoriesGames(): Promise<
    {
      categoryId: string;
      gamesIds?: number[];
      gamesBggIds?: number[];
      sortType?: SortType;
    }[]
  > {
    const categoriesGames: {
      categoryId: string;
      gamesIds?: number[];
      gamesBggIds?: number[];
      sortType?: SortType;
    }[] = [];

    for (const settings of this.categories.filter(
      (settings) => settings.file,
    )) {
      const games =
        (await this.getData<{ id?: number; bggId?: number }>(
          `${this.collectionsPath}categories/${settings.file}`,
        )) || [];

      if (games.length > 0) {
        const entry: {
          categoryId: string;
          gamesIds?: number[];
          gamesBggIds?: number[];
          sortType?: SortType;
        } = {
          categoryId: settings.id,
          sortType: settings.sortType,
          gamesIds: games.filter((game) => game.id).map((game) => game.id!),
          gamesBggIds: games
            .filter((game) => game.bggId)
            .map((game) => game.bggId!),
        };

        categoriesGames.push(entry);
      }
    }

    return categoriesGames;
  }

  async getGames(): Promise<GameRecord<Game>[]> {
    let allRecords: GameRecord<Game>[] = [];

    for (const collection of this.collections) {
      const records =
        (await this.getData<GameRecord<Game>>(
          `${this.collectionsPath}${collection.file}`,
        )) || [];
      const unavailableGames =
        (
          await this.getData<{ id: number }>(
            `${this.collectionsPath}${collection.unavailableFile}`,
          )
        ).map((value) => value.id) || [];
      const availableRecords = records.filter(
        (record) => !unavailableGames.includes(record.game.id),
      );
      availableRecords.forEach((record) => {
        record.owner = collection.owner;
      });

      allRecords = [...allRecords, ...availableRecords];
    }

    return this.distinctGame(allRecords.sort(this.sortGame));
  }

  private async getData<T>(fileName: string): Promise<T[]> {
    return fetch(fileName)
      .then((response) => response.json())
      .catch(() => []);
  }

  private convertGame(record: GameRecord<GameTesera>): GameRecord<Game> {
    const game = record.game;
    const rating = {
      bggRank: undefined,
      bggRating: game.bggRating,
      bggNumVotes: game.bggNumVotes,
      teseraRating: game.ratingUser,
      teseraNumVotes: game.numVotes,
      nastolioRating: undefined,
      nastolioNumVotes: undefined,
    };
    const newGame: Game = {
      id: game.id,
      alias: game.alias,
      teseraId: game.teseraId,
      nastolioId: undefined,
      bggId: undefined,
      title: game.title,
      titleOriginal: game.title2,
      photoUrl: game.photoUrl,
      year: game.year,
      rating: rating,
      playersMin: game.players_min,
      playersMax: game.players_max,
      timeMin: game.time_min,
      timeMax: game.time_max,
      age: game.age,
      tags: [],
      isAddition: game.isAddition,
      parentId: undefined,
    };

    return {
      relationId: record.relationId,
      creationDateUtc: record.creationDateUtc,
      comment: record.comment,
      game: newGame,
      owner: record.owner,
    };
  }

  private sortGame(a: GameRecord<Game>, b: GameRecord<Game>) {
    const nameA = a.game?.title?.toUpperCase(); // ignore upper and lowercase
    const nameB = b.game?.title?.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  }

  public sortGameByTopBgg(a: GameRecord<Game>, b: GameRecord<Game>) {
    const positionA =
      a.game?.badges?.find((badge) => badge.type === GameBadgeType.Top)
        ?.value ?? 999;
    const positionB =
      b.game?.badges?.find((badge) => badge.type === GameBadgeType.Top)
        ?.value ?? 999;

    if (positionA < positionB) {
      return -1;
    }
    if (positionA > positionB) {
      return 1;
    }

    return 0;
  }

  private distinctGame(records: GameRecord<Game>[]) {
    const unicGamesId = new Set();
    const unicGames: GameRecord<Game>[] = [];

    records.forEach((record) => {
      if (!unicGamesId.has(record.game.id)) {
        unicGamesId.add(record.game.id);
        unicGames.push(record);
      }
    });

    return unicGames;
  }
}
